<template>
  <div class="listing" v-if="queryData">
    <MetaTags v-if="metaInfo" :meta-info="metaInfo" />
    <!-- Hero -->
    <section v-if="queryData?.assets" class="listing__hero">
      <listing-hero :listing-images="listingImages"></listing-hero>
    </section>

    <!-- Navigation -->
    <sticky-navigation :anchor-links="anchorLinks" :contact-links="contactLinks">
      <template v-if="queryData?.agency">
        <agent-contact :agency="queryData?.agency"></agent-contact>
      </template>
    </sticky-navigation>

    <!-- Showings banner -->
    <!-- <div class="stripe showings" v-if="upcomingShowingsExist">
      <div class="listing-showings">
        <div class="listing-showings__content time">
          <span class="condo-has-showings" @click="scrollToApartments()">
            Seuraava esittely {{ nextShowing }} Katso tarkemmat
            <i>huoneistokohtaiset tiedot</i>
            .
          </span>
        </div>
      </div>
    </div> -->

    <!-- Basic info -->
    <div class="stripe basics">
      <div v-if="queryData" class="stripe__inner-container">
        <listing-basics :listing-data="queryData"></listing-basics>
      </div>
    </div>

    <!-- Apartments list -->
    <div id="apartments" class="stripe apartments" v-if="newHousingInfo">
      <div class="stripe__inner-container">
        <condominium-apartments
          :new-housing-info="newHousingInfo"
          :scrollToId="listingApartmentsId"
        ></condominium-apartments>
      </div>
    </div>

    <!-- Other info -->
    <div v-if="queryData" id="data-sheet">
      <div class="stripe">
        <div class="stripe__inner-container">
          <listing-data-sheet
            type="condominium"
            :listing-data="queryData"
            :scrollToId="listingDataSheetId"
          ></listing-data-sheet>
        </div>
      </div>
    </div>

    <!-- Loan banner -->
    <div class="stripe">
      <div id="banner" class="stripe__inner-container banner">
        <banner-full-width
          title="Asuntolaina - <br>Avain unelmiesi kotiin"
          disclaimer="Asuntolainan myöntää osuuspankki"
          image-url="https://op-koti.fi/assets/tabletin-kaytto-sohvalla.jpg"
          action-title="Tutustu OP:n asuntolainaan"
          action-url="https://www.op.fi/henkiloasiakkaat/lainat-ja-asunnot/asuntolaina"
        ></banner-full-width>
      </div>
    </div>

    <!-- Map -->
    <div id="listing-map" class="stripe map">
      <listing-map :base-info="queryData?.baseInfo" :scrollToId="listingMapId"></listing-map>
    </div>

    <!-- Demographics data -->
    <div id="listing-demographics" class="stripe" v-if="demographics">
      <div class="stripe__inner-container">
        <demographics :demographics="demographics"></demographics>
      </div>
    </div>

    <!-- Images -->
    <div class="stripe images" v-if="hasImages">
      <div id="images">
        <div class="stripe__inner-container image-list">
          <listing-images :images="listingImages" :scrollToId="listingImagesId"></listing-images>
        </div>
      </div>
    </div>

    <!-- Agency info -->
    <div v-if="queryData" class="stripe">
      <div class="stripe__inner-container">
        <listing-agency :agency="queryData?.agency" :listing="queryData"></listing-agency>
      </div>
    </div>

    <spinner :use-custom-property="true" :loading-property="isLoading"></spinner>
  </div>
</template>

<script lang="ts">
import * as actions from '@/app/store/action-types';
import config from '@/config';
import utils from '@/app/utils/utils';
import CondominiumApartments from './condominium-apartments.vue';
import BannerFullWidth from '@/app/components/banner-full-width/banner-full-width.vue';
import { condominium as condominiumRoute } from '@/app/routes';
import VueScrollTo from 'vue-scrollto';
import featureGroups from '@/config/featureGroups';
import { computed, defineAsyncComponent } from 'vue';
import Spinner from '@/app/components/spinner/spinner.vue';
import { useQueryClient } from '@tanstack/vue-query';
import useListingData from '@/app/data/useListingData';
import useDemographicsData from '@/app/data/useDemographicsData';
import listingUtils from '@/app/utils/listing-utils';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import isSSR from '@/utils/isSSR';
import MetaTags from '@/app/components/MetaTags.vue';

export default {
  name: 'condominium',
  data: () => ({
    listingApartmentsId: 'condominium-apartments-title',
    listingDataSheetId: 'listing-data-sheet-title',
    listingMapId: 'listing-map-title',
    listingImagesId: 'listing-images-title',
    config,
    featureGroups: featureGroups,
  }),
  setup() {
    const queryClient = useQueryClient();
    const route = useSafeRoute();
    const routeId = computed(() => {
      return getRouteParamAsString(route.value.params.id) || '';
    });
    const { data: queryData, isLoading } = useListingData(routeId);
    const newHousingInfo = computed(() => queryData?.value?.newHousingInfo);
    const postalCode = computed(() => queryData?.value?.baseInfo?.location?.postalCode);
    const { data: demographics } = useDemographicsData(postalCode);

    return {
      isLoading,
      queryData,
      queryClient,
      demographics,
      route,
      newHousingInfo,
    };
  },
  methods: {
    getPageTitle() {
      return `${this.queryData?.condominium ? this.queryData?.condominium.name : this.queryData?.baseInfo?.location?.streetAddress?.trim()} - OP Koti`;
    },
    getMetaDescription() {
      return (this.queryData?.baseInfo?.description && this.queryData.baseInfo.description.join(' ')) || '';
    },
    getSectionIds() {
      const ids = ['#apartments', '#data-sheet', '#contact-form'];
      if (this.queryData?.assets?.pictures) {
        ids.push('#images');
      }
      if (this.listingHasCoordinates) ids.push('#listing-map');
      return ids;
    },
    scrollToApartments() {
      const duration = 400;
      const options = {
        easing: 'ease-out',
        offset: -50,
        cancelable: true,
      };
      VueScrollTo.scrollTo(document.getElementById('apartments'), duration, options);
    },
  },

  watch: {
    queryData: {
      handler(newVal) {
        if (!isSSR && !!newVal) {
          const id = getRouteParamAsString(this.route.params.id);
          void this.$store.dispatch(actions.analytics.set, {
            pageName: `uudiskohde:${id?.toLowerCase()}`,
            eVar55: id?.toLowerCase(),
            eVar17: this.metaInfo.title,
            eVar57: newVal?.agency?.office?.id || newVal?.agency?.company?.id || '',
            eVar59: `${this.isRentalListing ? 'rent' : 'sale'};${newVal?.baseInfo?.listingType}`,
          });
          void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
          void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.propertyView);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    listingHasCoordinates() {
      const coordinates = this.queryData?.baseInfo?.location?.coordinates;
      return coordinates && coordinates.latitude && coordinates.longitude;
    },
    mainImage() {
      if (this.queryData?.assets?.mainPicture) {
        return this.queryData?.assets.mainPicture;
      }
      if (this.queryData?.assets?.pictures) {
        const mainPicAsset = this.queryData?.assets?.pictures?.find((img: any) => img.subType === 'mainPicture');
        return mainPicAsset ? mainPicAsset.url : null;
      }
      return null;
    },
    postalCode() {
      return this.queryData?.baseInfo?.location?.postalCode;
    },
    listingImages() {
      const pictures = this.queryData?.assets?.pictures;
      return pictures
        ? listingUtils.sortImages(pictures, listingUtils.getVirtualPresentation(this.queryData?.assets?.links))
        : [];
    },
    metaInfo() {
      const id = getRouteParamAsString(this.route.params.id)?.toLowerCase();
      const metaInfo = {
        title: this.getPageTitle(),
        meta: utils.getMetaTagsForPage(
          this.getPageTitle(),
          this.getMetaDescription(),
          this.queryData?.assets?.mainPicThumbnail || ''
        ),
        script: [],
        link: [
          {
            rel: 'canonical',
            href: `${config.FRONT.PROD_BASE_URL}${condominiumRoute.path}/${id}`,
          },
        ],
      };

      return metaInfo;
    },
    isRentalListing() {
      return this.queryData?.baseInfo?.featureGroups?.includes(this.featureGroups.RENTALS.id);
    },
    hasImages() {
      return this.listingImages?.length > 0;
    },
    anchorLinks() {
      const links = [
        {
          scrollToId: this.listingApartmentsId,
          iconClass: 'icon-apartment-circled',
          label: 'Asunnot',
        },
        {
          scrollToId: this.listingDataSheetId,
          iconClass: 'icon-info-circled',
          label: 'Tiedot',
        },
      ];

      if (this.listingHasCoordinates) {
        links.push({
          scrollToId: this.listingMapId,
          iconClass: 'icon-location-circled',
          label: 'Sijainti',
        });
      }

      if (this.hasImages) {
        links.push({
          scrollToId: this.listingImagesId,
          iconClass: 'icon-camera-circled',
          label: 'Kuvat',
        });
      }

      return links;
    },
    contactLinks() {
      return [
        {
          scrollToId: 'contact-form-title',
          iconClass: 'icon-speech-bubble-circled',
          label: 'Viesti',
        },
      ];
    },
    upcomingShowingsExist() {
      let showingsExist = false;
      if (this.newHousingInfo && this.newHousingInfo.listings) {
        this.newHousingInfo.listings.forEach((listing: any) => {
          if (listing.showings.length) {
            showingsExist = true;
          }
        });
      }
      return showingsExist;
    },
    nextShowing() {
      if (this.newHousingInfo && this.newHousingInfo.listings) {
        // Get all showings for all apartments in this condo
        const showings = this.newHousingInfo.listings.map((listing: any) => listing.showings);
        // Sort by closest showing date amd return the first one
        return showings.flat().sort((a: any, b: any) => a.date - b.date)[0].date;
      }
      return null;
    },
  },
  components: {
    StickyNavigation: defineAsyncComponent(() => import('@/app/components/sticky-navigation/sticky-navigation.vue')),
    AgentContact: defineAsyncComponent(() => import('./listing-agent-contact.vue')),
    ListingHero: defineAsyncComponent(() => import('./listing-hero.vue')),
    Demographics: defineAsyncComponent(() => import('@/app/components/demographics/demographics.vue')),
    BannerFullWidth,
    CondominiumApartments,
    ListingBasics: defineAsyncComponent(() => import('./listing-basics.vue')),
    ListingDataSheet: defineAsyncComponent(() => import('./listing-data-sheet.vue')),
    ListingImages: defineAsyncComponent(() => import('./listing-images.vue')),
    ListingMap: defineAsyncComponent(() => import('./listing-map.vue')),
    ListingAgency: defineAsyncComponent(() => import('./listing-agency.vue')),
    ListingPromos: defineAsyncComponent(() => import('./listing-promos.vue')),
    Spinner,
    MetaTags,
  },
};
</script>
